// src/components/EmailCapture.tsx (UPDATED - Handles Both Modes)

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AssessmentResult, ImplementationPlan } from '../types';
import { subscribeToConvertKit } from '../utils/convertkit';
import { generateValidatePDF, generateGettingStartedPDF } from '../utils/pdfGenerator';

interface EmailCaptureProps {
  mode: 'validate' | 'gettingStarted';
  result: AssessmentResult | null;
  implementationPlan: ImplementationPlan | null;
  onSubmit: (email: string) => void;
  onSkip: () => void;
}

const EmailCapture: React.FC<EmailCaptureProps> = ({
  mode,
  result,
  implementationPlan,
  onSubmit,
  onSkip,
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare custom fields based on mode
      let customFields = {};

      if (mode === 'validate' && result) {
        customFields = {
          score: result.percentage.toString(),
          risk_level: result.riskLevel,
          critical_gaps: result.criticalGaps.length.toString(),
        };
      } else if (mode === 'gettingStarted' && implementationPlan) {
        customFields = {
          timeline: implementationPlan.summary.estimatedTime,
          cloud_provider: implementationPlan.organizationProfile.cloudProvider,
          org_type: implementationPlan.organizationProfile.orgType,
          uses_ai: implementationPlan.organizationProfile.usesAI ? 'Yes' : 'No',
          stage: implementationPlan.organizationProfile.stage,
        };
      }

      // Subscribe to ConvertKit
      await subscribeToConvertKit(email, mode, customFields);

      // Generate and download PDF
      if (mode === 'validate' && result) {
        generateValidatePDF(result, email);
      } else if (mode === 'gettingStarted' && implementationPlan) {
        generateGettingStartedPDF(implementationPlan, email);
      }

      // Success - proceed to show results
      onSubmit(email);

    } catch (err) {
      console.error('ConvertKit subscription error:', err);
      setError('Failed to save your email. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Content varies by mode
  const title = mode === 'validate'
    ? 'Download Your Compliance Report'
    : 'Download Your Implementation Plan';

  const description = mode === 'validate'
    ? 'Enter your email to download your detailed compliance assessment and 30-day roadmap as a PDF'
    : 'Enter your email to download your customized 4-month implementation plan as a PDF';

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 mb-4">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {title}
          </h2>
          <p className="text-lg text-gray-600">
            {description}
          </p>
        </div>

        {/* Preview - Different for each mode */}
        {mode === 'validate' && result && (
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Your Compliance Score</p>
                <p className="text-4xl font-bold text-blue-600">{result.percentage}%</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Critical Gaps Found</p>
                <p className="text-4xl font-bold text-orange-600">{result.criticalGaps.length}</p>
              </div>
            </div>
          </div>
        )}

        {mode === 'gettingStarted' && implementationPlan && (
          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Implementation Timeline</p>
                <p className="text-3xl font-bold text-green-600">{implementationPlan.summary.estimatedTime}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Total Tasks</p>
                <p className="text-3xl font-bold text-green-600">
                  {implementationPlan.summary.totalItems}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Email Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
              disabled={isSubmitting}
            />
            {error && (
              <p className="text-red-600 text-sm mt-2">{error}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-4 rounded-lg font-bold text-lg transition ${
              isSubmitting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </span>
            ) : (
              mode === 'validate' ? '📧 Get My Roadmap PDF' : '📧 Get My Implementation Plan PDF'
            )}
          </button>
        </form>

        {/* What You'll Get - Different for each mode */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-3">
            What you'll receive:
          </p>
          <div className="space-y-2 text-sm text-gray-600">
            {mode === 'validate' ? (
              <>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Detailed 30-day compliance roadmap PDF with week-by-week tasks</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>CloudFormation templates and implementation guides for your gaps</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Weekly HIPAA compliance tips and best practices</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Invitation to free 30-minute compliance consultation</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>4-month implementation plan customized to your tech stack</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Cloud-specific configuration guides (AWS/Azure/GCP)</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Policy templates, checklists, and implementation resources</span>
                </div>
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Free 30-minute planning call to discuss your implementation</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Privacy Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            🔒 We respect your privacy. Unsubscribe anytime.
          </p>
          <button
            onClick={onSkip}
            className="text-sm text-gray-400 hover:text-gray-600 mt-2 underline"
          >
            Skip this step
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailCapture;
